import styled, { useTheme } from 'styled-components'
// @ts-ignore
import { Scrollama, Step } from 'react-scrollama'
import { useEffect, useRef, useState, createRef, RefObject } from 'react'
import { useGetSize } from 'Hooks/useGetSize'
import { StepContent, StepContainer, Strong } from '../../theme/styles'
import Dimmer from 'components/Dimmer'

const months = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juill.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
]

const stagesImages = [
  {
    src: require(`./images/stage-${1}-v2.png`),
    alt: 'stage-1',
    disappearedScale: 1,
  },
  {
    src: require(`./images/stage-${2}-v2.png`),
    alt: 'stage-1',
    disappearedScale: 0.85,
  },
  {
    src: require(`./images/stage-${3}-v2.png`),
    alt: 'stage-1',
    disappearedScale: 0.5,
  },
  {
    src: require(`./images/stage-${4}-v2.png`),
    alt: 'stage-1',
    disappearedScale: 0.65,
  },
  // {
  //   src: require(`./images/stage-${5}.png`),
  //   alt: 'stage-1',
  //   disappearedScale: 1,
  // },
  // {
  //   src: require(`./images/stage-${6}.png`),
  //   alt: 'stage-1',
  //   disappearedScale: 1,
  // },
]

const PlantsCalendar = () => {
  const theme = useTheme()
  const canvasRef = useRef<HTMLDivElement>(null)
  const transitionImageRef = useRef<HTMLImageElement>(null)
  const transitionImageBRef = useRef<HTMLImageElement>(null)
  const slidingContentRef = useRef<HTMLImageElement>(null)
  const debugRef = useRef<HTMLImageElement>(null)
  const plantsContainerRef = useRef<HTMLImageElement>(null)
  const [loadedImages, setLoadedImages] = useState(0)
  const totalImages = 4
  const { width } = useGetSize(slidingContentRef)
  const circleRef = useRef<SVGCircleElement>(null)
  const circleTextRef = useRef<SVGPathElement>(null)
  const calendarCircleRef = useRef<SVGSVGElement>(null)

  const growSegmentRef = useRef<RefObject<SVGCircleElement>[]>([])
  const plantNameRef = useRef<HTMLDivElement>(null)
  const circleMarkRef = useRef<HTMLDivElement>(null)
  const height = window.innerHeight
  const vmin = Math.min(window.innerWidth, window.innerHeight) * 0.3
  const rOut = vmin + 50
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })
  const [circleLength, setCircleLength] = useState(0)
  const [circleTextLength, setCircleTextLength] = useState(0)
  const [stage, setStage] = useState(0)

  const [plantsImageSize, setPlantsImageSize] = useState({
    width: 0,
    height: 0,
  })
  const imageTransitionRef = useRef<ImageTransitionHandle>(null)

  // console.log('Image size', plantsImageSize)

  const onStepEnter = (stepData: any) => {
    setStage(stepData.data)
  }

  const onStepProgress = (stepData: any) => {
    // Debug
    if (debugRef.current)
      debugRef.current.innerHTML = `Step ${
        stepData.data
      } progress: ${stepData.progress.toFixed(2)}`

    // Update the image transition
    if (stepData.data === 3) {
      imageTransitionRef.current?.setPct(stepData.progress)
    }

    // 3D to illustration transition
    if (stepData.data === 0) {
      // Decrease opacity of 3D model
      transitionImageRef.current?.style.setProperty(
        'opacity',
        (1 - stepData.progress).toString()
      )

      plantNameRef.current?.style.setProperty(
        'opacity',
        (1 - stepData.progress).toString()
      )

      // Increase opacity of illustration
      transitionImageBRef.current?.style.setProperty(
        'opacity',
        stepData.progress.toString()
      )

      // Animate indication circle
      circleMarkRef.current?.style.setProperty(
        'opacity',
        ((stepData.progress - 0.5) * 2).toString()
      )
    }

    // Zoom in
    else if (stepData.data === 1) {
      transitionImageBRef.current?.style.setProperty(
        'transform',
        `scale(${1 + stepData.progress})`
      )

      circleMarkRef.current?.style.setProperty(
        'transform',
        `translate(-56%, -50%) scale(${1 + stepData.progress})`
      )
    } else if (stepData.data === 2) {
      // Transition to bud
      transitionImageBRef.current?.style.setProperty(
        'transform',
        `scale(${2 + stepData.progress})`
      )
      plantsContainerRef.current?.style.setProperty(
        'opacity',
        stepData.progress.toString()
      )
      transitionImageBRef.current?.style.setProperty(
        'opacity',
        (1 - stepData.progress).toString()
      )

      circleMarkRef.current?.style.setProperty(
        'opacity',
        (1 - stepData.progress * 2).toString()
      )
      circleMarkRef.current?.style.setProperty(
        'transform',
        `translate(-56%, -50%) scale(${2 + stepData.progress})`
      )
    }
    // Time lapse
    else if (stepData.data === 3) {
      // calendarCircleRef.current?.style.setProperty(
      //   'transform',
      //   `rotate(${stepData.progress * 360}deg)`
      // )

      if (
        stepData.progress > 0.25 &&
        stepData.progress <= 0.5 &&
        stage !== 3.1
      ) {
        setStage(3.1)
      }

      if (
        stepData.progress > 0.5 &&
        stepData.progress <= 0.75 &&
        stage !== 3.2
      ) {
        setStage(3.2)
      }

      if (stepData.progress > 0.75 && stepData.progress <= 1 && stage !== 3.3) {
        setStage(3.3)
      }

      growSegmentRef.current[0]?.current?.style.setProperty(
        'stroke-dasharray',
        `${circleLength * 0.1 * stepData.progress} ${
          circleLength - circleLength * 0.1 * stepData.progress
        }`
      )
    } else if (stepData.data === 4) {
      growSegmentRef.current[1]?.current?.style.setProperty(
        'stroke-dasharray',
        `${cl(circleLengths[1], circleLength) * stepData.progress} ${
          circleLength - cl(circleLengths[1], circleLength) * stepData.progress
        }`
      )
    } else if (stepData.data === 5) {
      growSegmentRef.current[2]?.current?.style.setProperty(
        'stroke-dasharray',
        `${cl(circleLengths[2], circleLength) * stepData.progress} ${
          circleLength - cl(circleLengths[2], circleLength) * stepData.progress
        }`
      )
    } else if (stepData.data === 6) {
      growSegmentRef.current[3]?.current?.style.setProperty(
        'stroke-dasharray',
        `${cl(circleLengths[3], circleLength) * stepData.progress} ${
          circleLength - cl(circleLengths[3], circleLength) * stepData.progress
        }`
      )
    }
  }

  useEffect(() => {
    setImageSize({
      width: transitionImageRef.current?.width || 0,
      height: transitionImageRef.current?.height || 0,
    })
    setPlantsImageSize({
      width: plantsContainerRef.current?.width || 0,
      height: plantsContainerRef.current?.height || 0,
    })

    const pathLength = circleRef.current?.getTotalLength() || 0
    setCircleLength(pathLength)
    setCircleTextLength(circleTextRef.current?.getTotalLength() || 0)

    growSegmentRef.current = Array(4)
      .fill(null)
      .map(() => createRef<SVGCircleElement>())
  }, [])

  useEffect(() => {
    const updateDimensions = () => {
      if (transitionImageRef.current) {
        setImageSize({
          width: transitionImageRef.current.getBoundingClientRect().width,
          height: transitionImageRef.current.getBoundingClientRect().height,
        })
      }

      if (plantsContainerRef.current) {
        setPlantsImageSize({
          width: plantsContainerRef.current.getBoundingClientRect().width,
          height: plantsContainerRef.current.getBoundingClientRect().height,
        })
      }
    }

    // Si toutes les images sont chargées, mettez à jour les dimensions
    if (loadedImages === totalImages) {
      updateDimensions()
    }

    // Écouteur pour la redimension de la fenêtre
    window.addEventListener('resize', updateDimensions)

    // Nettoyage de l'écouteur
    return () => window.removeEventListener('resize', updateDimensions)
  }, [loadedImages]) // Dépend de loadedImages pour se réexécuter

  const handleImageLoad = () => {
    setLoadedImages((prev) => prev + 1)
  }

  const circlesSpace = 4
  const circleLengths = [0.425, 0.2375, 0.1, 0.2375]

  const cl = (pathLength: number, clength: number) => {
    return pathLength * clength - circlesSpace * 2
  }

  return (
    <Scrollyrtelling style={{backgroundColor: stage > 2 ? `rgb(${theme.coloredBackground})` : 'transparent'}}>
      <Dimmer targetRef={canvasRef} end={false} />
      <StickyChart ref={canvasRef}>
        {process.env.NODE_ENV !== 'production' && (
          <div style={{ position: 'fixed', top: 30, left: 0 }} ref={debugRef} />
        )}
        <PlantName ref={plantNameRef}>
          <Name>Marronnier</Name>
          <ScientificName>Aesculus hippocastanum</ScientificName>
        </PlantName>
        <Container ref={slidingContentRef}>
          <TransitionContainer>
            <TransitionImageContainer
              style={{ opacity: 0 }}
              ref={transitionImageBRef}
            >
              <TranstionImage
                src={require('./images/marronnier-transition-drawing-v2.png')}
              />
            </TransitionImageContainer>
            <TransitionImageContainer ref={transitionImageRef}>
              <TranstionImage
                src={require('./images/marronnier-transition-transparent.png')}
                onLoad={handleImageLoad}
              />
            </TransitionImageContainer>
            <Circle ref={circleMarkRef} />
          </TransitionContainer>
          <Plants ref={plantsContainerRef}>
            <Legend>
              <LegendBloc>
                <LegendCircle
                  style={{ background: `rgb(${theme.yellowColor})` }}
                />{' '}
                Croissance
              </LegendBloc>
              <LegendBloc>
                <LegendCircle
                  style={{ background: `rgb(${theme.greenColorBright})` }}
                />{' '}
                Dormance
              </LegendBloc>
            </Legend>
            <svg width={width} height={height}>
              <path
                d={`
                M ${width * 0.5}, ${height * 0.5 - rOut}
                a ${rOut},${rOut} 0 1,0 0,${rOut * 2} 
                a ${rOut},${rOut} 0 1,0 0,${-rOut * 2}`}
                fill='none'
                stroke='red'
                id='TextPathOutside'
                ref={circleTextRef}
                strokeWidth='0'
              />
              <g
                style={{
                  transformOrigin: `${width * 0.5}px ${height * 0.5}px`,
                }}
                ref={calendarCircleRef}
              >
                {/* Croissance */}
                <CalendarCircle
                  style={{
                    strokeDasharray: `${cl(circleLengths[0], circleLength)} ${
                      circleLength - cl(circleLengths[0], circleLength)
                    }`,
                    strokeDashoffset: circlesSpace,
                    stroke: `rgba(${theme.yellowColor}, 0.55)`,
                  }}
                  ref={circleRef}
                  cx={`${width * 0.5}px`}
                  cy={`${height * 0.5}px`}
                />

                {/* Paradormance */}
                <CalendarCircle
                  style={{
                    strokeDasharray: `${cl(circleLengths[1], circleLength)} ${
                      circleLength - cl(circleLengths[1], circleLength)
                    }`,
                    strokeDashoffset:
                      circleLength -
                      cl(circleLengths[0], circleLength) -
                      circlesSpace,
                    stroke: `rgba(${theme.greenColorBright}, 0.35)`,
                  }}
                  cx={`${width * 0.5}px`}
                  cy={`${height * 0.5}px`}
                />

                {/* Endodormance */}
                <CalendarCircle
                  style={{
                    strokeDasharray: `${cl(circleLengths[2], circleLength)} ${
                      circleLength - cl(circleLengths[2], circleLength)
                    }`,
                    strokeDashoffset:
                      circleLength -
                      cl(circleLengths[0], circleLength) -
                      cl(circleLengths[1], circleLength) -
                      circlesSpace * 3,
                    stroke: `rgba(${theme.greenColorBright}, 0.55)`,
                  }}
                  cx={`${width * 0.5}px`}
                  cy={`${height * 0.5}px`}
                />

                {/* Écodormance */}
                <CalendarCircle
                  style={{
                    strokeDasharray: `${cl(circleLengths[3], circleLength)} ${
                      circleLength - cl(circleLengths[3], circleLength)
                    }`,
                    strokeDashoffset:
                      circleLength -
                      cl(circleLengths[0], circleLength) -
                      cl(circleLengths[1], circleLength) -
                      cl(circleLengths[2], circleLength) -
                      circlesSpace * 5,
                    stroke: `rgba(${theme.greenColorBright}, 0.75)`,
                  }}
                  cx={`${width * 0.5}px`}
                  cy={`${height * 0.5}px`}
                />

                {/* Animation croissance */}
                <GrowCalendarCirlce
                  style={{
                    strokeDasharray: `${0} ${circleLength}`,
                    strokeDashoffset: circlesSpace,
                    stroke: `rgba(${theme.yellowColor}, 0.25)`,
                    mixBlendMode: 'plus-lighter',
                  }}
                  ref={growSegmentRef.current[0]}
                  cx={`${width * 0.5}px`}
                  cy={`${height * 0.5}px`}
                />

                {/* Animation paradormance */}
                <GrowCalendarCirlce
                  style={{
                    strokeDasharray: `${0} ${circleLength}`,
                    strokeDashoffset:
                      circleLength -
                      cl(circleLengths[0], circleLength) -
                      circlesSpace,
                    stroke: `rgba(${theme.greenColorBright}, 0.25)`,
                    mixBlendMode: 'plus-lighter',
                  }}
                  ref={growSegmentRef.current[1]}
                  cx={`${width * 0.5}px`}
                  cy={`${height * 0.5}px`}
                />

                {/* Endodormance */}
                <GrowCalendarCirlce
                  style={{
                    strokeDasharray: `${0} ${circleLength}`,
                    strokeDashoffset:
                      circleLength -
                      cl(circleLengths[0], circleLength) -
                      cl(circleLengths[1], circleLength) -
                      circlesSpace * 3,
                    stroke: `rgba(${theme.greenColorBright}, 0.25)`,
                    mixBlendMode: 'plus-lighter',
                  }}
                  ref={growSegmentRef.current[2]}
                  cx={`${width * 0.5}px`}
                  cy={`${height * 0.5}px`}
                />

                {/* Écodormance */}
                <GrowCalendarCirlce
                  style={{
                    strokeDasharray: `${0} ${circleLength}`,
                    strokeDashoffset:
                      circleLength -
                      cl(circleLengths[0], circleLength) -
                      cl(circleLengths[1], circleLength) -
                      cl(circleLengths[2], circleLength) -
                      circlesSpace * 5,
                    stroke: `rgba(${theme.greenColorBright}, 0.25)`,
                    mixBlendMode: 'plus-lighter',
                  }}
                  ref={growSegmentRef.current[3]}
                  cx={`${width * 0.5}px`}
                  cy={`${height * 0.5}px`}
                />
              </g>
              {/* <text>
                <CircleLabel
                  textAnchor='middle'
                  dominantBaseline='middle'
                  startOffset={
                    circleTextLength * 0.75 -
                    cl(circleLengths[0], circleTextLength) * 0.5
                  }
                  href='#TextPathOutside'
                >
                  Croissance
                </CircleLabel>
                <CircleLabel
                  textAnchor='middle'
                  dominantBaseline='middle'
                  startOffset={
                    circleTextLength * 0.75 -
                    cl(circleLengths[0], circleTextLength) -
                    cl(circleLengths[1], circleTextLength) * 0.5
                  }
                  href='#TextPathOutside'
                >
                  Paradormance
                </CircleLabel>
                <CircleLabel
                  textAnchor='middle'
                  dominantBaseline='middle'
                  startOffset={
                    circleTextLength * 0.75 -
                    cl(circleLengths[0], circleTextLength) -
                    cl(circleLengths[1], circleTextLength) -
                    cl(circleLengths[2], circleTextLength) * 0.5
                  }
                  href='#TextPathOutside'
                >
                  Endodormance
                </CircleLabel>
                <CircleLabel
                  textAnchor='middle'
                  dominantBaseline='middle'
                  startOffset={
                    circleTextLength -
                    cl(circleLengths[3], circleTextLength) * 0.75
                  }
                  href='#TextPathOutside'
                >
                  Écodormance
                </CircleLabel>
              </text> */}
              {months.map((month, i) => {
                const angle = (i / 12) * 360 - 90
                const x = width * 0.5 + rOut * Math.cos((angle * Math.PI) / 180)
                const y = height * 0.5 + rOut * Math.sin((angle * Math.PI) / 180)
                return (
                  <MonthLabel
                    textAnchor='middle'
                    key={i}
                    x={x}
                    y={y}
                    style={{
                      // transform: `rotate(${(i / 12) * 360}deg)`,
                      // transformOrigin: `${width * 0.5}px ${height * 0.5}px`,
                      fill: `rgba(${theme.textColorOnBackground}, 0.85)`,
                      textTransform: 'capitalize',
                    }}
                  >
                    {month}
                  </MonthLabel>
                )
              })}
            </svg>
            <PlantContainer>
              <img
                src={stagesImages[0].src}
                style={{
                  transition: 'all 800ms ease-in-out',
                  width: '100%',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  opacity: [2, 3, 4, 5, 6].includes(stage) ? 1 : 0,
                }}
              />
              <img
                src={stagesImages[1].src}
                style={{
                  transition: 'all 800ms ease-in-out',
                  width: '100%',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  opacity: [3.1].includes(stage) ? 1 : 0,
                }}
              />
              <img
                src={stagesImages[2].src}
                style={{
                  transition: 'all 800ms ease-in-out',
                  width: '100%',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  opacity: [3.2].includes(stage) ? 1 : 0,
                }}
              />
              <img
                src={stagesImages[3].src}
                style={{
                  transition: 'all 800ms ease-in-out',
                  width: '100%',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  opacity: [3.3].includes(stage) ? 1 : 0,
                }}
              />
              {/* <ImageTransition images={stagesImages} ref={imageTransitionRef} /> */}
            </PlantContainer>
          </Plants>
        </Container>
      </StickyChart>
      <ScrollytellingContainer>
        <Scrollama
          offset={1}
          onStepEnter={onStepEnter}
          onStepProgress={onStepProgress}
        >
          <Step data={0} key={`scrollama-step-${0}`}>
            <StepContainer />
          </Step>
          <Step data={1} key={`scrollama-step-${1}`}>
            <StepContainer />
          </Step>
          <Step data={2} key={`scrollama-step-${2}`}>
            <StepContainer />
          </Step>
          <Step data={3} key={`scrollama-step-${3}`}>
            <StepContainer style={{ paddingBottom: '300vh' }}>
              <StepContent>
                <div>
                  <Strong color='yellow'>Croissance</Strong>
                </div>
                <div style={{ fontFamily: 'var(--font-sans-serif)' }}>
                  <strong>Du printemps à l'été</strong>
                </div>
                <p>
                  Lorsque les conditions deviennent favorables, les bourgeons
                  sortent de leur dormance et commencent à croître. C'est aussi
                  durant cette période que les fruits se développent après la
                  floraison.
                </p>
              </StepContent>
            </StepContainer>
          </Step>
          <Step data={4} key={`scrollama-step-${4}`}>
            <StepContainer>
              <StepContent>
                <div>
                  <Strong>Paradormance</Strong>
                </div>
                <div style={{ fontFamily: 'var(--font-sans-serif)' }}>
                  <strong>De la fin de l'été à l'automne</strong>
                </div>
                <p>
                  Durant cette période, la croissance des nouveaux bourgeons est
                  inibée par des facteurs endogènes. Ils resteront dormants plus
                  de la moitié de l’année.
                </p>
              </StepContent>
            </StepContainer>
          </Step>
          <Step data={5} key={`scrollama-step-${5}`}>
            <StepContainer>
              <StepContent>
                <div>
                  <Strong>Endodormance</Strong>
                </div>
                <div style={{ fontFamily: 'var(--font-sans-serif)' }}>
                  <strong>De la fin de l'automne à l'hiver</strong>
                </div>
                <p>
                  Les bourgeons entrent en dormance profonde en réponse à la
                  diminution de la lumière du jour et aux températures plus
                  froides. Ils nécessitent une période de froid pour lever cette
                  dormance.
                </p>
              </StepContent>
            </StepContainer>
          </Step>
          <Step data={6} key={`scrollama-step-${6}`}>
            <StepContainer>
              <StepContent>
                <div>
                  <Strong>Écodormance</Strong>
                </div>
                <div style={{ fontFamily: 'var(--font-sans-serif)' }}>
                  <strong>De la fin de l'hiver au début du printemps</strong>
                </div>

                <p>
                  Les bourgeons sont prêts à se développer, mais les conditions
                  environnementales défavorables (températures encore basses,
                  manque d'eau) empêchent leur croissance.
                </p>
              </StepContent>
            </StepContainer>
          </Step>
        </Scrollama>
      </ScrollytellingContainer>
    </Scrollyrtelling>
  )
}

const Container = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  color: ${({ theme }) => `rgb(${theme.textColorOnBackground})`};
  // background-color: ${({ theme }) => `rgb(${theme.coloredBackground})`};
`

const Plants = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
`

const PlantContainer = styled.div`
  text-align: center;
  position: absolute;
  transform: translate(-50%, -50%);
  top: 50%;
  left: 50%;
  height: 75vmin;
  width: 75vmin;

  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 150vmin;
    width: 150vmin;
  }
`

const MonthLabel = styled.text`
  font-size: ${({ theme }) => theme.textS};
`

const Illustration = styled.img`
  display: block;
  height: 75vmin;
  width: 75vmin;

  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 100vmin;
    width: 100vmin;
  }
`

const CalendarCircle = styled.circle`
  r: ${60 * 0.5}vmin;
  fill: none;
  stroke-width: 50px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    r: ${90 * 0.5}vmin;
  }
`

const GrowCalendarCirlce = styled(CalendarCircle)`
stroke-width: 80px;

${({ theme }) => theme.breakpoints.down('md')} {
  stroke-width: 70px;
}
`

const TransitionImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
`

const TranstionImage = styled(Illustration)`
  aspect-ratio: 1;
  flex: 0 0 auto;
`

const Circle = styled.div`
  // 50% of the smallest side of the screen
  width: 5vmin;
  height: 5vmin;
  border: 1px solid ${({ theme }) => `rgb(${theme.greenColorBright})`};
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-56%, -50%);
  opacity: 0;
`

const PlantName = styled.div`
  text-align: center;
  color: white;
  position: absolute;
  bottom: 4vh;
  left: 50%;
  transform: translateX(-50%);
`

const TransitionContainer = styled(PlantContainer)`
  height: 75vmin;
  width: 75vmin;

  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 100vmin;
    width: 100vmin;
  }
`

const Name = styled.div`
  font-weight: bold;
  line-height: 1.4;
`

const ScientificName = styled.div`
  font-style: italic;
  font-size: ${({ theme }) => theme.textS};
  line-height: 1.4;
  opacity: 0.65;
`

const StickyChart = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100vh;
`

const Scrollyrtelling = styled.div`
  position: relative;
  z-index: 1;
  // background-color: ${({ theme }) => `rgb(${theme.coloredBackground})`};
  margin-bottom: 60px;
`

const ScrollytellingContainer = styled.div`
  position: relative;
  width: 100%;
  pointer-events: none;
`

const LegendCircle = styled.div`
  margin-right: 5px;
  display: inline-block;
  width: 24px;
  height: 16px;
  top: 0;
  left: 0;
  transition: opacity 0.3s ease;
`

const Legend = styled.div`
  position: absolute;
  top: 5vh;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.textS};

  ${({ theme }) => theme.breakpoints.down('md')} {
    top: 15vh;
  }
`

const LegendBloc = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  transition: all 0.4s ease-in-out;
  overflow: hidden;

  &:last-child {
    margin-right: 0;
  }
`

const CircleLabel = styled('textPath')`
  font-size: 1em;
  font-family: var(--font-sans-serif);
  fill: white;
  -webkit-font-smoothing: antialiased;
`

export default PlantsCalendar
